import { useEffect, useState } from 'react';

import { UploadOutlined } from '@ant-design/icons';
import { Button, Form, Input, message, Modal, Spin, Upload } from 'antd';
import { RcFile, UploadFile } from 'antd/lib/upload';
import Lottie from 'lottie-react';

import { useAppSelector, useAppDispatch } from '@/Hooks/useAppRedux';

import { getStreamerBanner } from '@/Axios/api/userRole';

import SNSConnectionTable from './SNSConnectionTable';

import type { SnsPlatform } from '../interface/I_userRole';

export interface StreamDetail {
  name?: string;
  description?: string;
  banner?: RcFile[];
  isBannerDelete?: boolean;
  snsPlatforms?: SnsPlatform[];
}

const StreamDetailModal = ({
  open,
  onConfirm,
  setModalOpen,
}: {
  open: boolean;
  onConfirm: (values: StreamDetail) => void;
  setModalOpen: (value: boolean) => void;
}) => {
  const { loadingStreamerData } = useAppSelector((state) => state.userRole);
  const { viewingStreamerData } = useAppSelector((state) => state.userRole.value);
  const [snsPlatforms, setSnsPlatforms] = useState<SnsPlatform[]>([]);

  const [form] = Form.useForm();
  const [preview, setPreview] = useState<string | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);

  useEffect(() => {
    if (open && viewingStreamerData.uuid) {
      form.setFieldsValue({
        name: viewingStreamerData.name || '',
        description: viewingStreamerData.description || '',
        banner: viewingStreamerData.bannerUrl
          ? ([
              {
                uid: '1',
                name: 'preview.lottie.json',
                status: 'done',
              },
            ] as UploadFile[])
          : [],
        isBannerDelete: false,
        snsPlatforms: viewingStreamerData.snsPlatforms || [],
      });

      if (viewingStreamerData.bannerUrl) {
        getStreamerBanner(viewingStreamerData.bannerUrl).then(({ data }) => setPreview(data));
      }
      setSnsPlatforms(viewingStreamerData.snsPlatforms || []);
    }
  }, [
    form,
    open,
    viewingStreamerData,
  ]);

  useEffect(() => {
    form.setFieldValue('snsPlatforms', snsPlatforms);
  }, [form, snsPlatforms]);

  const onConfirmClick = async () => {
    const values = form.getFieldsValue(true);
    onConfirm(values);
    setModalOpen(false);
  };

  const beforeUpload = (file: RcFile) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        setPreview(JSON.parse(reader.result as string));
        form.setFieldsValue({ isBannerDelete: false });
      } catch (ex) {
        message.error({
          content: 'Invalid Lottie File Format',
          duration: 3,
          style: {
            marginTop: '120px',
          },
        });
      }
    };

    reader.readAsText(file);
    return false;
  };

  const handleAddSnsConnection = (record: Partial<SnsPlatform>) => {
    setSnsPlatforms((prev) => [...prev, record as SnsPlatform]);
  };

  const handleEditSnsConnection = (idx: number, record: Partial<SnsPlatform>) => {
    setSnsPlatforms((prev) => prev.map((item, i) => (i === idx ? { ...item, ...record } : item)));
  };

  const handleDeleteSnsConnection = (idx: number) => {
    setSnsPlatforms((prev) => prev.filter((_, i) => i !== idx));
  };

  const onRemoveBanner = () => {
    setPreview(null);
    form.setFieldsValue({
        banner: [],
        isBannerDelete: true
    });
};

  return (
    <>
      <Modal
        open={open}
        destroyOnClose
        onOk={onConfirmClick}
        onCancel={() => setModalOpen(false)}
        title="Stream Detail"
      >
        <Spin spinning={loadingStreamerData}>
          <Form form={form} preserve={false} layout="vertical">
            <Form.Item name="name" label="Name">
              <Input />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input />
            </Form.Item>
            <Form.Item
              name="banner"
              label="Banner"
              valuePropName="fileList"
              getValueFromEvent={(e) => e.fileList}
            >
              <Upload
                maxCount={1}
                beforeUpload={beforeUpload}
                listType="picture"
                onPreview={() => setPreviewVisible(true)}
                onRemove={onRemoveBanner}
                accept=".json,application/json"
              >
                <Button icon={<UploadOutlined />}>Click to Upload</Button>
              </Upload>
            </Form.Item>
            <div style={{ marginTop: 24 }}>
              <SNSConnectionTable
                data={Array.isArray(snsPlatforms) ? snsPlatforms : []}
                loading={false}
                onAdd={handleAddSnsConnection}
                onEdit={handleEditSnsConnection}
                onDelete={handleDeleteSnsConnection}
              />
            </div>
          </Form>
        </Spin>
      </Modal>
      <Modal
        open={previewVisible}
        footer={null}
        title="Preview"
        onCancel={() => setPreviewVisible(false)}
      >
        <Lottie animationData={preview} />
      </Modal>
    </>
  );
};

export default StreamDetailModal;
